import { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Calendar, RefreshCw, Download, Plus, ExternalLink, Search, X,
  AlertCircle, Trash2, MapPin, Hash, Layers, CheckSquare, Square, Send, FileText, Link2, Unlink, Image as ImageIcon, LayoutTemplate, Eye
} from 'lucide-react'
import { useSchedulerApi } from './useSchedulerApi'
import { gameContext, seasonYears } from './templates'
import TemplateBuilder from './TemplateBuilder'
import ScheduleModal from './ScheduleModal'
import TeamImages from './TeamImages'
import ThumbTemplates from './ThumbTemplates'
import RegenerateModal from './RegenerateModal'
import ThumbPreview from './ThumbPreview'
import './TeamScheduler.css'

// Team Scheduler — full-season game schedules for the leagues we cover, one
// season per (league, division, year). PIHL is the first league. The vader
// worker scrapes each season's SportsEngine site into sched_games; adding a
// league or a season is a pasted URL from the UI, not a deploy.

const SEASON_KEY = 'teamsched.seasonId'
const SPORTS = ['hockey', 'football', 'basketball', 'baseball', 'softball', 'soccer', 'lacrosse', 'volleyball', 'wrestling', 'other']
const RANGES = [
  { id: 'upcoming', label: 'Upcoming' },
  { id: 'all', label: 'All' },
  { id: 'past', label: 'Past' },
]

const todayIso = () => {
  // Local ET date as YYYY-MM-DD — the schedule's dates are ET calendar dates.
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' })
    .format(new Date())
}

function fmtDayHeading(iso) {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

function fmtMonthChip(iso) {
  const [y, m] = iso.split('-').map(Number)
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
}

// ---- week math (all on UTC-midnight dates so DST never shifts a day) ----
const dayMs = 86400000
const isoToUtc = (iso) => { const [y, m, d] = iso.split('-').map(Number); return Date.UTC(y, m - 1, d) }
// Monday on or before the given date.
function mondayOf(iso) {
  const ms = isoToUtc(iso)
  const dow = new Date(ms).getUTCDay()           // 0 = Sun … 6 = Sat
  return ms - ((dow + 6) % 7) * dayMs
}
function fmtShort(ms) {
  return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
}

const host = (s) => (s?.source_base || '').replace(/^https?:\/\//, '') || 'the league site'

function fmtTime(t) {
  return (t || '').replace(/\s*(EDT|EST|ET)\s*$/i, '') || 'TBD'
}

function fmtSynced(iso) {
  if (!iso) return 'never synced'
  const d = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : iso.replace(' ', 'T') + 'Z')
  return 'synced ' + d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

export default function TeamScheduler() {
  const sched = useSchedulerApi()

  const [seasons, setSeasons] = useState([])
  const [seasonId, setSeasonId] = useState(() => {
    try { return Number(localStorage.getItem(SEASON_KEY)) || null } catch { return null }
  })
  const [games, setGames] = useState([])
  const [teamRows, setTeamRows] = useState([])   // sched_teams: name, division, division_group
  const [images, setImages] = useState([])       // sched_images for the season's league
  const [imgOpen, setImgOpen] = useState(false)
  const [thumbTemplates, setThumbTemplates] = useState([])
  const [thumbOpen, setThumbOpen] = useState(false)
  const [regenOpen, setRegenOpen] = useState(false)
  const [previewGame, setPreviewGame] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [syncing, setSyncing] = useState(false)
  const [notice, setNotice] = useState(null)   // { kind: 'ok'|'err', text }

  const [q, setQ] = useState('')
  const [team, setTeam] = useState('')
  const [venue, setVenue] = useState('')
  const [range, setRange] = useState('upcoming')
  const [month, setMonth] = useState('')
  const [week, setWeek] = useState(null)      // game-week number, or null
  const [dhOnly, setDhOnly] = useState(false)
  const [division, setDivision] = useState('')  // division_group: AAA | AA | A | D2
  const [pickedOnly, setPickedOnly] = useState(false)
  const [templates, setTemplates] = useState([])
  const [tplOpen, setTplOpen] = useState(false)
  const [schedOpen, setSchedOpen] = useState(false)
  const [pickBusy, setPickBusy] = useState(false)

  const [addOpen, setAddOpen] = useState(false)
  const [addLabel, setAddLabel] = useState('')
  const [addLeague, setAddLeague] = useState('')
  const [addSport, setAddSport] = useState('hockey')
  const [addUrl, setAddUrl] = useState('')
  const [addBusy, setAddBusy] = useState(false)

  const season = useMemo(() => seasons.find(s => s.id === seasonId) || null, [seasons, seasonId])
  const league = season?.league || ''
  useEffect(() => {
    if (!league) { setTemplates([]); return }
    sched.listTemplates(league).then(d => setTemplates(d.templates || [])).catch(() => setTemplates([]))
    sched.listThumbTemplates(league).then(d => setThumbTemplates(d.templates || [])).catch(() => setThumbTemplates([]))
  }, [league, sched])
  const leagues = useMemo(() => [...new Set(seasons.map(s => s.league || 'League'))], [seasons])

  const loadSeasons = useCallback(async () => {
    const { seasons: list } = await sched.listSeasons()
    setSeasons(list)
    setSeasonId(prev => (list.some(s => s.id === prev) ? prev : (list[0]?.id ?? null)))
    return list
  }, [sched])

  const loadGames = useCallback(async (id) => {
    if (!id) { setGames([]); setLoading(false); return }
    setLoading(true)
    setError('')
    try {
      const { games: rows, teams: tr, images: im } = await sched.listGames(id)
      setGames(rows)
      setTeamRows(tr || [])
      setImages(im || [])
    } catch (e) {
      setError(e.message)
      setGames([]); setTeamRows([]); setImages([])
    } finally {
      setLoading(false)
    }
  }, [sched])

  useEffect(() => {
    loadSeasons().catch(e => { setError(e.message); setLoading(false) })
  }, [loadSeasons])

  useEffect(() => {
    loadGames(seasonId)
    try { if (seasonId) localStorage.setItem(SEASON_KEY, String(seasonId)) } catch { /* ignore */ }
    setTeam(''); setVenue(''); setMonth(''); setWeek(null); setDhOnly(false); setDivision('')
  }, [seasonId, loadGames])

  useEffect(() => {
    if (!notice) return
    const t = setTimeout(() => setNotice(null), 8000)
    return () => clearTimeout(t)
  }, [notice])

  async function handleSync() {
    if (!seasonId || syncing) return
    setSyncing(true)
    setNotice(null)
    try {
      const r = await sched.sync(seasonId)
      setNotice({ kind: 'ok', text: `Synced ${r.upserted} games across ${r.dates} dates${r.removed ? `, ${r.removed} removed` : ''} (${(r.ms / 1000).toFixed(1)}s)` })
      await loadSeasons()
      await loadGames(seasonId)
    } catch (e) {
      setNotice({ kind: 'err', text: e.body?.error || e.message })
      loadSeasons().catch(() => {})
    } finally {
      setSyncing(false)
    }
  }

  async function handleAddSeason(e) {
    e.preventDefault()
    if (!addLabel.trim() || !addLeague.trim() || !addUrl.trim()) return
    setAddBusy(true)
    try {
      const { season: created } = await sched.createSeason({ label: addLabel.trim(), league: addLeague.trim(), sport: addSport, url: addUrl.trim() })
      setAddOpen(false); setAddLabel(''); setAddUrl('')
      await loadSeasons()
      setSeasonId(created.id)
      setNotice({ kind: 'ok', text: `Added ${created.league} · ${created.label}. Sync to pull its schedule.` })
    } catch (err) {
      setNotice({ kind: 'err', text: err.body?.error || err.message })
    } finally {
      setAddBusy(false)
    }
  }

  async function handleDeleteSeason() {
    if (!season) return
    if (!window.confirm(`Delete "${season.label}" and its ${season.game_count} games from vader? (The league site is not affected.)`)) return
    try {
      await sched.deleteSeason(season.id)
      setSeasonId(null)
      await loadSeasons()
    } catch (err) {
      setNotice({ kind: 'err', text: err.body?.error || err.message })
    }
  }

  // ---- derived ----
  const teams = useMemo(() => {
    const s = new Set()
    games.forEach(g => { s.add(g.visitor); s.add(g.home) })
    return [...s].sort((a, b) => a.localeCompare(b))
  }, [games])

  const venues = useMemo(() => {
    const s = new Set(games.map(g => g.location).filter(Boolean))
    return [...s].sort((a, b) => a.localeCompare(b))
  }, [games])

  const months = useMemo(() => {
    const s = new Set(games.map(g => g.game_date.slice(0, 7)))
    return [...s].sort()
  }, [games])

  const today = todayIso()

  // Game weeks: only Mon–Fri weeks that actually have games are numbered, so a
  // holiday break doesn't burn a week number. Computed from the unfiltered
  // season so numbers stay stable no matter what's filtered out.
  const weekIndex = useMemo(() => {
    const mondays = [...new Set(games.map(g => mondayOf(g.game_date)))].sort((a, b) => a - b)
    return new Map(mondays.map((ms, i) => [ms, i + 1]))
  }, [games])
  const weekOf = useCallback((iso) => weekIndex.get(mondayOf(iso)) ?? 0, [weekIndex])

  // [{ n, start, months: Set('YYYY-MM') }] — months lets a picked week light up
  // its month chip(s), and a picked month dim the weeks outside it.
  const weekList = useMemo(() => {
    const byWeek = new Map()
    games.forEach(g => {
      const n = weekOf(g.game_date)
      if (!byWeek.has(n)) byWeek.set(n, { n, start: mondayOf(g.game_date), months: new Set() })
      byWeek.get(n).months.add(g.game_date.slice(0, 7))
    })
    return [...byWeek.values()].sort((a, b) => a.n - b.n)
  }, [games, weekOf])
  const selectedWeek = useMemo(() => weekList.find(w => w.n === week) || null, [weekList, week])

  // Double headers: 2+ games at the same rink on the same day — the preferred
  // games to cover (one crew, one venue). Keyed on the unfiltered season so a
  // team filter still shows that a game is half of a double header.
  const doubleHeaders = useMemo(() => {
    const counts = new Map()
    games.forEach(g => {
      if (!g.location) return
      const k = `${g.game_date}|${g.location}`
      counts.set(k, (counts.get(k) || 0) + 1)
    })
    return new Set([...counts].filter(([, n]) => n >= 2).map(([k]) => k))
  }, [games])
  const isDh = useCallback((g) => g.location && doubleHeaders.has(`${g.game_date}|${g.location}`), [doubleHeaders])

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return games.filter(g => {
      if (range === 'upcoming' && g.game_date < today) return false
      if (range === 'past' && g.game_date >= today) return false
      if (week) { if (weekOf(g.game_date) !== week) return false }
      else if (month && !g.game_date.startsWith(month)) return false
      if (team && g.visitor !== team && g.home !== team) return false
      if (venue && g.location !== venue) return false
      if (dhOnly && !isDh(g)) return false
      if (division && g.division_group !== division) return false
      if (pickedOnly && !g.picked) return false
      if (needle) {
        const hay = `${g.visitor} ${g.home} ${g.location || ''} ${g.game_number || ''}`.toLowerCase()
        if (!hay.includes(needle)) return false
      }
      return true
    })
  }, [games, q, team, venue, range, month, week, weekOf, dhOnly, isDh, division, pickedOnly, today])

  // filtered → [{ week, start, end, count, days: [[date, rows], …] }]
  const weeks = useMemo(() => {
    const byDay = new Map()
    filtered.forEach(g => {
      if (!byDay.has(g.game_date)) byDay.set(g.game_date, [])
      byDay.get(g.game_date).push(g)
    })
    const out = []
    for (const [date, rows] of byDay) {
      const week = weekOf(date)
      let w = out[out.length - 1]
      if (!w || w.week !== week) {
        const start = mondayOf(date)
        w = { week, start, end: start + 4 * dayMs, count: 0, days: [] }
        out.push(w)
      }
      w.days.push([date, rows])
      w.count += rows.length
    }
    return out
  }, [filtered, weekOf])
  const groups = weeks

  const nextGame = useMemo(() => games.find(g => g.game_date >= today) || null, [games, today])
  const hasFilter = q || team || venue || month || week || dhOnly || division || pickedOnly || range !== 'upcoming'

  // ---- selection (picks persist on the game row) ----
  const pickedGames = useMemo(() => games.filter(g => g.picked), [games])
  const pickedInCatalog = useMemo(() => pickedGames.filter(g => g.tbtv_id).length, [pickedGames])
  const years = useMemo(() => (games.length ? seasonYears(games[0].game_date, games[games.length - 1].game_date) : ''), [games])
  const ctxFor = useCallback((g) => gameContext(g, season, { week: weekOf(g.game_date), years }), [season, weekOf, years])

  // team name → logo url (league-wide); league logo = the asset whose name says "logo"
  const imgMap = useMemo(() => new Map(images.filter(i => i.kind !== 'asset' && i.image_url).map(i => [i.name, i.image_url])), [images])
  const imageOf = useCallback((name) => imgMap.get(name) || '', [imgMap])
  const leagueLogo = useMemo(() => images.find(i => i.kind === 'asset' && /logo/i.test(i.name) && i.image_url)?.image_url || '', [images])
  const colorMap = useMemo(() => new Map(images.filter(i => i.primary_color).map(i => [i.name, i.primary_color])), [images])
  // Per-game sources for the thumbnail renderer (logos + team colors).
  const sourcesFor = useCallback((g) => ({
    'away-logo': imgMap.get(g.visitor) || '', 'home-logo': imgMap.get(g.home) || '', 'league-logo': leagueLogo,
    'away-color': colorMap.get(g.visitor) || '', 'home-color': colorMap.get(g.home) || '',
  }), [imgMap, colorMap, leagueLogo])

  async function setPicked(ids, picked) {
    if (!ids.length || !seasonId) return
    const idSet = new Set(ids.map(String))
    // optimistic
    setGames(gs => gs.map(g => (idSet.has(g.game_id) ? { ...g, picked: picked ? 1 : 0 } : g)))
    setPickBusy(true)
    try {
      await sched.setPicks(seasonId, [...idSet], picked)
    } catch (e) {
      setNotice({ kind: 'err', text: e.body?.error || e.message })
      loadGames(seasonId)
    } finally {
      setPickBusy(false)
    }
  }
  const togglePick = (g) => setPicked([g.game_id], !g.picked)
  const visibleIds = filtered.map(g => g.game_id)
  const allVisiblePicked = filtered.length > 0 && filtered.every(g => g.picked)

  async function unlink(g) {
    if (!window.confirm(`Unlink this game from its catalog item? The catalog item stays; the game can be scheduled again.`)) return
    try {
      await sched.unlinkGame(g.game_id)
      setGames(gs => gs.map(x => (x.game_id === g.game_id ? { ...x, tbtv_id: null, scheduled_mode: null, scheduled_at: null } : x)))
    } catch (e) {
      setNotice({ kind: 'err', text: e.body?.error || e.message })
    }
  }

  function onScheduled(r) {
    if (r.created.length) setNotice({ kind: 'ok', text: `Created ${r.created.length} catalog ${r.created.length === 1 ? 'item' : 'items'}${r.errors.length ? `, ${r.errors.length} failed` : ''}.` })
    loadGames(seasonId)
  }

  // Division groups present in the season, in league order, with game counts.
  const divisionGroups = useMemo(() => {
    const order = ['AAA', 'AA', 'A', 'D2']
    const counts = new Map()
    games.forEach(g => { if (g.division_group) counts.set(g.division_group, (counts.get(g.division_group) || 0) + 1) })
    return [...counts.keys()]
      .sort((a, b) => (order.indexOf(a) + 1 || 99) - (order.indexOf(b) + 1 || 99) || a.localeCompare(b))
      .map(k => ({ key: k, count: counts.get(k) }))
  }, [games])

  // Team dropdown grouped by full division ("Class AAA", "A Gold", …), falling
  // back to a flat list when the season has no standings data yet.
  const teamGroups = useMemo(() => {
    if (!teamRows.length) return null
    const byDiv = new Map()
    teamRows.forEach(t => { if (!byDiv.has(t.division)) byDiv.set(t.division, []); byDiv.get(t.division).push(t.name) })
    const known = new Set(teamRows.map(t => t.name))
    const other = teams.filter(t => !known.has(t))
    if (other.length) byDiv.set('Other', other)
    return [...byDiv.entries()].map(([d, names]) => [d, names.sort((a, b) => a.localeCompare(b))])
  }, [teamRows, teams])
  // Month chips shown pressed: the picked week's month(s), else the picked month.
  const pressedMonths = selectedWeek ? selectedWeek.months : new Set(month ? [month] : [])

  function clearFilters() {
    setQ(''); setTeam(''); setVenue(''); setMonth(''); setWeek(null); setDhOnly(false); setDivision(''); setPickedOnly(false); setRange('upcoming')
  }

  function pickMonth(mo) {
    setWeek(null)
    setMonth(prev => (prev === mo ? '' : mo))
    setRange('all')
  }

  // Picking a week also sets the month chip to match (first month of the week).
  function pickWeek(w) {
    if (week === w.n) { setWeek(null); return }
    setWeek(w.n)
    setMonth([...w.months].sort()[0])
    setRange('all')
  }

  // ---- render ----
  return (
    <div className="ts">
      <header className="ts-head">
        <div className="ts-title">
          <h1><Calendar size={20} /> Team Scheduler</h1>
          <span className="muted">Full-season game schedules by league — PIHL first</span>
        </div>
        <div className="ts-actions">
          <select
            value={seasonId ?? ''}
            onChange={e => setSeasonId(Number(e.target.value) || null)}
            aria-label="Season"
            disabled={!seasons.length}
          >
            {!seasons.length && <option value="">No seasons</option>}
            {leagues.map(l => (
              <optgroup key={l} label={l}>
                {seasons.filter(s => s.league === l).map(s => (
                  <option key={s.id} value={s.id}>{s.label}{s.is_active ? '' : ' (inactive)'}</option>
                ))}
              </optgroup>
            ))}
          </select>
          <button onClick={handleSync} disabled={!seasonId || syncing} title={`Re-pull the whole season from ${host(season)}`}>
            <RefreshCw size={14} className={syncing ? 'spin' : ''} /> {syncing ? 'Syncing…' : `Sync from ${season?.league || 'league'}`}
          </button>
          {seasonId && games.length > 0 && (
            <a className="ts-btn" href={sched.csvUrl(seasonId)} download>
              <Download size={14} /> CSV
            </a>
          )}
          {season && (
            <button onClick={() => setTplOpen(true)} title="Title & description templates for this league">
              <FileText size={14} /> Templates
            </button>
          )}
          {season && (
            <button onClick={() => setImgOpen(true)} title="Team logos and league images">
              <ImageIcon size={14} /> Team Images
            </button>
          )}
          {season && (
            <button onClick={() => setThumbOpen(true)} title="Thumbnail templates — Canva plate + per-game layers">
              <LayoutTemplate size={14} /> Thumbnails
            </button>
          )}
          <button onClick={() => setAddOpen(o => !o)} aria-expanded={addOpen} title="Add a season — any league on a SportsEngine site">
            <Plus size={14} /> Season
          </button>
        </div>
      </header>

      {addOpen && (
        <form className="card ts-add" onSubmit={handleAddSeason}>
          <div className="form-group">
            <label htmlFor="ts-add-league">League</label>
            <input
              id="ts-add-league" list="ts-league-options" value={addLeague} onChange={e => setAddLeague(e.target.value)}
              placeholder="PIHL" required
            />
            <datalist id="ts-league-options">
              {leagues.map(l => <option key={l} value={l} />)}
            </datalist>
          </div>
          <div className="form-group">
            <label htmlFor="ts-add-sport">Sport</label>
            <select id="ts-add-sport" value={addSport} onChange={e => setAddSport(e.target.value)}>
              {SPORTS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="ts-add-label">Season label</label>
            <input id="ts-add-label" value={addLabel} onChange={e => setAddLabel(e.target.value)} placeholder="Varsity 2026-27" required />
          </div>
          <div className="form-group ts-add-url">
            <label htmlFor="ts-add-url">Schedule URL (SportsEngine site)</label>
            <input
              id="ts-add-url" type="url" inputMode="url" value={addUrl} onChange={e => setAddUrl(e.target.value)}
              placeholder="https://www.<league-site>.com/schedule/day/league_instance/…?subseason=…" required
            />
          </div>
          <div className="ts-add-actions">
            <button type="submit" disabled={addBusy}>{addBusy ? 'Adding…' : 'Add season'}</button>
            <button type="button" onClick={() => setAddOpen(false)}>Cancel</button>
          </div>
          <p className="muted ts-add-hint">
            On the league's SportsEngine site pick the division + season, then paste the page URL. The site, <span className="mono">league_instance</span> and <span className="mono">subseason</span> are read from it. Divisions come from that season's standings page.
          </p>
        </form>
      )}

      {notice && (
        <div className={`ts-notice ${notice.kind}`} role="status">
          {notice.kind === 'err' && <AlertCircle size={14} />}
          <span>{notice.text}</span>
          <button className="ts-notice-x" onClick={() => setNotice(null)} aria-label="Dismiss"><X size={14} /></button>
        </div>
      )}

      {season && (
        <div className="ts-meta">
          <span className="ts-meta-league">{season.league}{season.sport ? ` · ${season.sport}` : ''}</span>
          <span>{fmtSynced(season.last_synced_at)}</span>
          {season.last_sync_status === 'error' && (
            <span className="text-danger" title={season.last_sync_error || ''}>last sync failed</span>
          )}
          <a href={season.source_url} target="_blank" rel="noreferrer">
            {host(season)} <ExternalLink size={11} />
          </a>
          <button className="ts-linkbtn" onClick={handleDeleteSeason} title="Remove this season from vader">
            <Trash2 size={12} /> remove
          </button>
        </div>
      )}

      {season && games.length > 0 && (
        <div className="ts-stats">
          <div className="ts-stat"><strong>{games.length}</strong><span>games</span></div>
          <div className="ts-stat"><strong>{teams.length}</strong><span>teams</span></div>
          <div className="ts-stat"><strong>{venues.length}</strong><span>venues</span></div>
          <div className="ts-stat"><strong>{new Set(games.map(g => g.game_date)).size}</strong><span>game dates</span></div>
          <div className="ts-stat"><strong>{weekIndex.size}</strong><span>game weeks</span></div>
          <div className="ts-stat is-dh"><strong>{doubleHeaders.size}</strong><span>double headers</span></div>
          <div className="ts-stat">
            <strong>{nextGame ? fmtDayHeading(nextGame.game_date) : '—'}</strong>
            <span>{nextGame ? `next game day · week ${weekOf(nextGame.game_date)}` : 'season over'}</span>
          </div>
        </div>
      )}

      {season && games.length > 0 && (
        <div className="ts-filters">
          <div className="ts-search">
            <Search size={14} />
            <input
              type="search" value={q} onChange={e => setQ(e.target.value)}
              placeholder="Team, venue, game #" aria-label="Search games"
            />
          </div>
          <select value={team} onChange={e => setTeam(e.target.value)} aria-label="Team">
            <option value="">All teams</option>
            {teamGroups
              ? teamGroups.map(([d, names]) => (
                  <optgroup key={d} label={d}>
                    {names.map(t => <option key={t} value={t}>{t}</option>)}
                  </optgroup>
                ))
              : teams.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={venue} onChange={e => setVenue(e.target.value)} aria-label="Venue">
            <option value="">All venues</option>
            {venues.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
          <div className="ts-seg" role="group" aria-label="Date range">
            {RANGES.map(r => (
              <button
                key={r.id} type="button" aria-pressed={range === r.id}
                onClick={() => { setRange(r.id); setMonth(''); setWeek(null) }}
              >{r.label}</button>
            ))}
          </div>
          <button
            type="button" className="ts-toggle" aria-pressed={dhOnly}
            onClick={() => setDhOnly(v => !v)}
            title="Only games that share a rink with another game the same day"
          >
            <Layers size={14} /> Double headers
          </button>
          <button
            type="button" className="ts-toggle ts-toggle-pick" aria-pressed={pickedOnly}
            onClick={() => setPickedOnly(v => !v)}
            title="Only games marked to broadcast"
          >
            <CheckSquare size={14} /> Selected{pickedGames.length ? ` (${pickedGames.length})` : ''}
          </button>
          {hasFilter && (
            <button type="button" className="ts-clear" onClick={clearFilters}><X size={13} /> Clear</button>
          )}
          {divisionGroups.length > 0 && (
            <div className="ts-divisions" role="group" aria-label="Division">
              {divisionGroups.map(d => (
                <button
                  key={d.key} type="button" className="ts-chip ts-div-chip"
                  aria-pressed={division === d.key}
                  onClick={() => setDivision(prev => (prev === d.key ? '' : d.key))}
                  title={`${d.count} games`}
                >{d.key}</button>
              ))}
            </div>
          )}
          <div className="ts-weeks" role="group" aria-label="Game week">
            {weekList.map(w => (
              <button
                key={w.n} type="button"
                className={`ts-chip ts-week-chip ${month && !week && !w.months.has(month) ? 'is-dim' : ''}`}
                aria-pressed={week === w.n}
                onClick={() => pickWeek(w)}
                title={`Week ${w.n} · ${fmtShort(w.start)} – ${fmtShort(w.start + 4 * dayMs)}`}
              >
                <span>Wk {w.n}</span>
                <span className="ts-week-chip-date">{fmtShort(w.start)}</span>
              </button>
            ))}
          </div>
          <div className="ts-months" role="group" aria-label="Month">
            {months.map(mo => (
              <button
                key={mo} type="button" className="ts-chip" aria-pressed={pressedMonths.has(mo)}
                onClick={() => pickMonth(mo)}
              >{fmtMonthChip(mo + '-01')}</button>
            ))}
          </div>
        </div>
      )}

      {/* ---- states ---- */}
      {loading && <div className="card ts-empty"><p className="muted">Loading schedule…</p></div>}

      {!loading && error && (
        <div className="card ts-empty">
          <AlertCircle size={18} className="text-danger" />
          <p>{error}</p>
          <button onClick={() => loadGames(seasonId)}>Retry</button>
        </div>
      )}

      {!loading && !error && !seasons.length && (
        <div className="card ts-empty">
          <p>No seasons yet.</p>
          <p className="muted">Add one with a league's SportsEngine schedule URL, then sync.</p>
          <button onClick={() => setAddOpen(true)}><Plus size={14} /> Add season</button>
        </div>
      )}

      {!loading && !error && season && !games.length && (
        <div className="card ts-empty">
          <p>{season.label} has no games in vader yet.</p>
          <p className="muted">Sync pulls every game date from {host(season)} — roughly one page per game day, 10–20 seconds.</p>
          <button onClick={handleSync} disabled={syncing}>
            <RefreshCw size={14} className={syncing ? 'spin' : ''} /> {syncing ? 'Syncing…' : `Sync from ${season.league}`}
          </button>
        </div>
      )}

      {!loading && !error && games.length > 0 && !groups.length && (
        <div className="card ts-empty">
          <p>No games match.</p>
          <button onClick={clearFilters}>Clear filters</button>
        </div>
      )}

      {/* ---- schedule ---- */}
      {!loading && !error && games.length > 0 && (
        <div className="ts-selbar" role="region" aria-label="Selection">
          <button type="button" className="ts-selbar-all" onClick={() => setPicked(visibleIds, !allVisiblePicked)} disabled={pickBusy || !filtered.length}
            title={allVisiblePicked ? 'Unselect every game in view' : 'Select every game in view'}>
            {allVisiblePicked ? <CheckSquare size={15} /> : <Square size={15} />} {allVisiblePicked ? 'Unselect' : 'Select'} all in view
          </button>
          <span className="ts-selbar-count">
            <strong>{pickedGames.length}</strong> selected to broadcast
            {pickedInCatalog > 0 && <span className="muted"> · {pickedInCatalog} in catalog</span>}
          </span>
          <span className="ts-spacer" />
          {pickedGames.length > 0 && (
            <button type="button" className="ts-linkbtn" onClick={() => setPicked(pickedGames.map(g => g.game_id), false)} disabled={pickBusy}>
              <X size={13} /> Clear selection
            </button>
          )}
          <button type="button" onClick={() => setSchedOpen(true)} disabled={!pickedGames.some(g => !g.tbtv_id)} title="Create catalog items for the selected games">
            <Send size={14} /> Schedule selected{pickedGames.length - pickedInCatalog > 0 ? ` (${pickedGames.length - pickedInCatalog})` : ''}
          </button>
          <button type="button" onClick={() => setRegenOpen(true)} disabled={!pickedInCatalog} title="Re-render thumbnails / re-apply templates / move start times for selected games already in the catalog">
            <RefreshCw size={14} /> Regenerate{pickedInCatalog ? ` (${pickedInCatalog})` : ''}
          </button>
        </div>
      )}

      {!loading && !error && groups.length > 0 && (
        <div className="ts-list">
          <div className="ts-count muted">
            {filtered.length === games.length ? `${games.length} games` : `${filtered.length} of ${games.length} games`}
            {week && ` · Week ${week}`}
            {division && ` · ${division}`}
            {team && ` · ${team}`}
            {venue && ` · ${venue}`}
          </div>
          {groups.map(w => {
            const isThisWeek = isoToUtc(today) >= w.start && isoToUtc(today) < w.start + 7 * dayMs
            return (
              <section key={w.week} className="ts-week">
                <h2 className="ts-week-head">
                  <span className="ts-week-num">Week {w.week}</span>
                  <span className="ts-week-range">{fmtShort(w.start)} – {fmtShort(w.end)}</span>
                  {isThisWeek && <span className="badge warning">This week</span>}
                  <span className="muted">{w.count} {w.count === 1 ? 'game' : 'games'} · {w.days.length} {w.days.length === 1 ? 'day' : 'days'}</span>
                </h2>
                {w.days.map(([date, rows]) => (
                  <section key={date} className="ts-day">
                    <h3 className="ts-day-head">
                      <span>{fmtDayHeading(date)}</span>
                      {date === today && <span className="badge warning">Today</span>}
                      <span className="muted">{rows.length} {rows.length === 1 ? 'game' : 'games'}</span>
                      {rows.some(isDh) && (
                        <span className="ts-dh-tag" title="Two or more games at the same rink today">
                          <Layers size={11} /> {new Set(rows.filter(isDh).map(g => g.location)).size === 1 ? 'double header' : `${new Set(rows.filter(isDh).map(g => g.location)).size} double headers`}
                        </span>
                      )}
                    </h3>
                    <div className="ts-rows">
                      {rows.map(g => (
                        <GameRow key={g.game_id} g={g} dh={isDh(g)} team={team} onTeam={setTeam} onVenue={setVenue} onPick={togglePick} onUnlink={unlink} imageOf={imageOf} onPreview={setPreviewGame} canPreview={thumbTemplates.length > 0} />
                      ))}
                    </div>
                  </section>
                ))}
              </section>
            )
          })}
        </div>
      )}

      {tplOpen && season && (
        <TemplateBuilder
          league={league} templates={templates} api={sched} onChange={setTemplates} onClose={() => setTplOpen(false)}
          sampleGames={(pickedGames.length ? pickedGames : games.filter(g => g.game_date >= today)).slice(0, 40)}
          ctxFor={ctxFor}
        />
      )}
      {schedOpen && season && (
        <ScheduleModal
          season={season} picked={pickedGames} allGames={games} isDh={isDh} templates={templates}
          ctxFor={ctxFor} imageOf={imageOf} leagueLogo={leagueLogo} thumbTemplates={thumbTemplates} sourcesFor={sourcesFor}
          api={sched} onDone={onScheduled} onClose={() => setSchedOpen(false)}
        />
      )}
      {previewGame && season && (
        <ThumbPreview
          season={season} game={previewGame} thumbTemplates={thumbTemplates} ctxFor={ctxFor} sourcesFor={sourcesFor} api={sched}
          onApplied={(id, url) => setGames(gs => gs.map(x => (x.game_id === id ? { ...x, thumbnail_url: url } : x)))}
          onClose={() => setPreviewGame(null)}
        />
      )}
      {regenOpen && season && (
        <RegenerateModal
          season={season} games={pickedGames.filter(g => g.tbtv_id)} templates={templates} thumbTemplates={thumbTemplates}
          ctxFor={ctxFor} sourcesFor={sourcesFor} api={sched}
          onDone={(r) => { if (r.updated.length) setNotice({ kind: 'ok', text: `Regenerated ${r.updated.length} catalog ${r.updated.length === 1 ? 'item' : 'items'}${r.errors.length ? `, ${r.errors.length} failed` : ''}.` }); loadGames(seasonId) }}
          onClose={() => setRegenOpen(false)}
        />
      )}
      {thumbOpen && season && (
        <ThumbTemplates
          league={league} templates={thumbTemplates} api={sched} onChange={setThumbTemplates} onClose={() => setThumbOpen(false)}
          sampleGames={(pickedGames.length ? pickedGames : games.filter(g => g.game_date >= today)).slice(0, 40)}
          ctxFor={ctxFor} sourcesFor={sourcesFor}
        />
      )}
      {imgOpen && season && (
        <TeamImages league={league} teams={teamRows} images={images} api={sched} onChange={setImages} onClose={() => setImgOpen(false)} />
      )}
    </div>
  )
}

function GameRow({ g, dh, team, onTeam, onVenue, onPick, onUnlink, imageOf, onPreview, canPreview }) {
  const hasScore = g.visitor_score != null || g.home_score != null
  const cls = ['ts-row', dh ? 'is-dh' : '', g.picked ? 'is-picked' : '', g.tbtv_id ? 'is-linked' : '', g.status !== 'scheduled' ? `status-${g.status}` : ''].join(' ').trim()
  const TeamBtn = ({ name, side }) => (
    <button
      type="button"
      className={`ts-team ${team === name ? 'is-focus' : ''}`}
      onClick={() => onTeam(team === name ? '' : name)}
      title={`Filter to ${name}`}
    >
      {imageOf(name) ? <img className="ts-team-logo" src={imageOf(name)} alt="" loading="lazy" /> : null}
      {name}
      {team === name && <span className="ts-side">{side}</span>}
    </button>
  )
  return (
    <div className={cls}>
      <label className="ts-pick" title={g.picked ? 'Selected to broadcast — click to unselect' : 'Select to broadcast'}>
        <input type="checkbox" checked={!!g.picked} onChange={() => onPick(g)} aria-label={`Select ${g.visitor} at ${g.home}`} />
      </label>
      <div className="ts-time" title={g.game_time || ''}>{fmtTime(g.game_time)}</div>
      <div className="ts-matchup">
        <TeamBtn name={g.visitor} side="AWAY" />
        {hasScore ? (
          <span className="ts-score mono">{g.visitor_score ?? '–'}–{g.home_score ?? '–'}</span>
        ) : (
          <span className="ts-at">at</span>
        )}
        <TeamBtn name={g.home} side="HOME" />
        {g.division && <span className="ts-div-tag" title="Division (from the league standings page)">{g.division.replace(/^Class /, '')}</span>}
        {g.status !== 'scheduled' && <span className={`badge ts-status ${g.status}`}>{g.status.replace('_', ' ')}</span>}
        {g.tbtv_id && (
          <span className="ts-linked" title={`Catalog item ${g.tbtv_id}`}>
            <a href={`/ondemand/catalog?select=${g.tbtv_id}`} target="_blank" rel="noreferrer"><Link2 size={11} /> {g.scheduled_mode === 'ondemand' ? 'On-Demand' : 'Live event'}</a>
            <button type="button" className="ts-iconbtn ts-iconbtn-xs" onClick={() => onUnlink(g)} title="Unlink from catalog item" aria-label="Unlink from catalog item"><Unlink size={11} /></button>
          </span>
        )}
      </div>
      <button type="button" className="ts-venue" onClick={() => onVenue(g.location || '')} title="Filter to this venue">
        <MapPin size={12} /> {g.location || 'TBD'}
        {dh && <span className="ts-dh-pill" title="Double header — another game at this rink today">DH</span>}
      </button>
      <div className="ts-num mono" title="League game number"><Hash size={11} />{g.game_number || '—'}</div>
      <button type="button" className="ts-link ts-preview-btn" onClick={() => onPreview(g)} disabled={!canPreview} title={canPreview ? 'Preview thumbnail' : 'Add a thumbnail template first'} aria-label="Preview thumbnail">
        <Eye size={14} />
      </button>
      <a className="ts-link" href={g.source_url} target="_blank" rel="noreferrer" title="Open on the league site" aria-label="Open on the league site">
        <ExternalLink size={14} />
      </a>
    </div>
  )
}
